import { POWERUP_LIST } from "../data/powerups";
import { AvatarAnimation } from "../types/animation";
import { GameState } from "../types/game";
import { createDefaultAvatarState } from "./avatar";
import { computeDerivedStats } from "./powerups";

export const getDefaultGameState = (): GameState => {
  return {
    avatarState: createDefaultAvatarState(),
    avatarReaction: null,
    run: {
      status: "live",
    },
    activeAnimation: null,
    streamTitle: {
      unlocked: false,
      value: "Late Night Stream",
    },
    cooldowns: {
      animations: {},
    },
    viewers: {
      real: 0,
      botted: 0,
    },
    likes: 0,
    powerups: {},
    simulation: {
      badCommentProgress: 0,
      chatProgress: 0,
      giftProgress: 0,
    },
    unlocks: {
      animations: {},
      clothing: {},
      chat: false,
      music: {
        lofi: false,
      },
    },
    buffs: [],
  };
};

export const updateGame = (state: GameState, dt: number): GameState => {
  if (state.run.status !== "live") {
    return state;
  }

  const nextBuffs = state.buffs
    .map((buff) => ({
      ...buff,
      remaining: Math.max(0, buff.remaining - dt),
    }))
    .filter((buff) => buff.remaining > 0);

  const stateAfterBuffs: GameState = {
    ...state,
    buffs: nextBuffs,
  };

  const stats = computeDerivedStats(stateAfterBuffs, POWERUP_LIST);

  const nextRealViewers = Math.max(
    0,
    stateAfterBuffs.viewers.real +
      (stats.realViewerFlowPerSecond -
        stateAfterBuffs.viewers.real * stats.realViewerDecayPerSecond) *
        dt,
  );

  const nextLikes = stateAfterBuffs.likes + stats.likesPerSecond * dt;

  const nextAnimationCooldowns: GameState["cooldowns"]["animations"] = {
    ...stateAfterBuffs.cooldowns.animations,
  };
  const nextActiveAnimation =
    stateAfterBuffs.activeAnimation == null
      ? null
      : {
          ...stateAfterBuffs.activeAnimation,
          remaining: Math.max(0, stateAfterBuffs.activeAnimation.remaining - dt),
        };

  for (const animationId of Object.keys(
    nextAnimationCooldowns,
  ) as AvatarAnimation[]) {
    nextAnimationCooldowns[animationId] = Math.max(
      0,
      (nextAnimationCooldowns[animationId] ?? 0) - dt,
    );
  }

  return {
    ...stateAfterBuffs,
    likes: nextLikes,
    viewers: {
      ...stateAfterBuffs.viewers,
      real: nextRealViewers,
    },
    activeAnimation:
      nextActiveAnimation && nextActiveAnimation.remaining > 0
        ? nextActiveAnimation
        : null,
    cooldowns: {
      ...stateAfterBuffs.cooldowns,
      animations: nextAnimationCooldowns,
    },
    simulation: {
      ...stateAfterBuffs.simulation,
      chatProgress:
        stateAfterBuffs.simulation.chatProgress +
        stats.chatEventsPerSecond * dt,
      badCommentProgress:
        stateAfterBuffs.simulation.badCommentProgress +
        stats.badCommentsPerSecond * dt,
      giftProgress:
        stateAfterBuffs.simulation.giftProgress + stats.giftsPerSecond * dt,
    },
  };
};

export function addTimedBuff<TBuff extends GameState["buffs"][number]>(
  state: GameState,
  buff: TBuff,
): GameState {
  return {
    ...state,
    buffs: [...state.buffs, buff],
  };
}
